/**
 * Lift2Fit — admin: Profile & settings (`#/admin/profile`).
 *
 * The founder's account page, and deliberately NOT the customer one. The admin
 * shell is a third product surface: its account link used to fall through to
 * the lifter profile, which offered training fields, the coaching relationship
 * and a persona badge that mean nothing to the platform operator. This page
 * answers the questions an operator has about their own account — who am I,
 * what may I do, what am I accepted into, how does my screen look — and leaves
 * every business setting to the admin screens that own it.
 *
 * Nothing here changes entitlements: plan, seats and support hands are decided
 * on the account and plan screens, never by the account editing itself.
 */
import { useEffect, useState } from 'react';
import { api, type PolicyConsentList, type ApiResult } from '@/lib/api';
import { cn } from '@/lib/format';
import { useSession } from '@/lib/useSession';
import { useTheme, type Theme } from '@/lib/useTheme';
import { useUnitPreference } from '@/lib/useUnitPreference';
import { Button } from '@/components/ui';
import { ThemeToggle } from '@/components/ThemeToggle';
import { ShieldIcon } from '@/lib/icons';
import {
  AppLinkButton,
  Field,
  LiveMessage,
  Panel,
  ScreenHeader,
  StatusChip,
} from '../components';
import { ChatIcon } from '../chat';
import { Link, navigate } from '../router';
import { LEGAL_DOCS, acceptedDateLabel } from '../legal/policy';
import { ADMIN_EYEBROW, AdminRow, coachPlanLabel, roleLabel, roleTone } from './shared';

const TIMEZONES = [
  'UTC',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Madrid',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Rome',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Australia/Sydney',
] as const;

export function AdminProfileScreen() {
  const session = useSession();
  const user = session.user;
  const { theme, setTheme } = useTheme();
  const { unit, setUnit } = useUnitPreference();

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [timezone, setTimezone] = useState(user?.timezone ?? 'UTC');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<'info' | 'success' | 'danger'>('info');
  // `null` = still loading; a failed read is reported, never invented.
  const [consents, setConsents] = useState<ApiResult<PolicyConsentList> | null>(null);

  useEffect(() => {
    let alive = true;
    void api.consents().then((result) => {
      if (alive) setConsents(result);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!user) {
    return (
      <div className="flex flex-col gap-4 border border-hairline bg-surface p-6">
        <h1 className="font-display text-2xl font-black uppercase text-ink">Not signed in</h1>
        <p className="text-[15px] text-ink-muted">
          Sign in with the administrator account to see the platform profile.
        </p>
        <AppLinkButton to="/login" variant="outline">
          Sign in
        </AppLinkButton>
      </div>
    );
  }

  const nameChanged = displayName.trim() !== '' && displayName.trim() !== user.displayName;

  /**
   * Every write below persists to the account, then mirrors the answer back into
   * the session so the rest of the shell (and the next reload) agrees with it.
   */
  const save = async (patch: { displayName?: string; unitSystem?: 'metric' | 'imperial'; timezone?: string }) => {
    setBusy(true);
    const result = await api.updateMe(patch);
    setBusy(false);
    if (!result.data) {
      setTone('danger');
      setMessage(result.problem?.detail ?? 'That setting did not save. Try again.');
      return false;
    }
    session.updateUser({
      displayName: result.data.displayName,
      unitSystem: result.data.unitSystem,
      timezone: result.data.timezone,
    });
    setTimezone(result.data.timezone);
    setDisplayName(result.data.displayName);
    setTone('success');
    setMessage('Saved to the account.');
    return true;
  };

  const consentList = consents?.data ?? null;
  const roles = user.roles;

  return (
    <div className="flex flex-col gap-6" data-testid="admin-profile">
      <ScreenHeader
        eyebrow={ADMIN_EYEBROW}
        title="Profile & settings"
        lead="The operator’s own account — identity, access, display preferences and the consent record. Customer settings live in their own surfaces."
        badge={<StatusChip tone="danger" icon={<ShieldIcon />}>Admin</StatusChip>}
        actions={<ThemeToggle />}
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <Panel
          testId="admin-profile-identity"
          title="Identity"
          subtitle="The name other surfaces greet you by, and the address that signs you in."
        >
          <Field label="Display name">
            <input
              data-testid="admin-profile-name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              maxLength={80}
              className="h-11 w-full border border-hairline bg-page px-3 text-[15px] text-ink"
            />
          </Field>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              data-testid="admin-profile-save"
              disabled={busy || !nameChanged}
              onClick={() => void save({ displayName: displayName.trim() })}
            >
              {busy ? 'Saving…' : 'Save name'}
            </Button>
            <span className="text-[12px] text-ink-muted">
              {nameChanged ? 'Unsaved change.' : 'Matches the account.'}
            </span>
          </div>
          <div className="flex flex-col">
            <AdminRow label="Email" value={user.email} />
            <AdminRow label="Account id" value={user.id} valueTitle={user.id} />
            <AdminRow
              label="Access"
              value={
                <span className="flex flex-wrap justify-end gap-1">
                  {roles.map((role) => (
                    <StatusChip key={role} tone={roleTone(role)}>
                      {roleLabel(role)}
                    </StatusChip>
                  ))}
                </span>
              }
            />
            <AdminRow
              label="Plan"
              value={user.coachPlan === 'free' ? 'No paid plan' : coachPlanLabel(user.coachPlan)}
            />
          </div>
          <LiveMessage message={message} tone={tone} />
        </Panel>

        <div className="flex flex-col gap-4">
          <Panel
            testId="admin-profile-preferences"
            title="Preferences"
            subtitle="Units change display only; canonical storage stays kilograms."
          >
            <div className="flex flex-col gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Units</span>
              <div className="flex flex-wrap gap-2">
                {(
                  [
                    ['metric', 'Kilograms'],
                    ['imperial', 'Pounds'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    data-testid={`admin-unit-${value}`}
                    aria-pressed={unit === value}
                    onClick={() => {
                      setUnit(value);
                      void save({ unitSystem: value });
                    }}
                    className={cn(
                      'h-11 border px-4 text-[12px] font-bold uppercase tracking-[0.08em]',
                      unit === value
                        ? 'border-ink bg-ink text-ink-inverse'
                        : 'border-hairline bg-surface text-ink hover:border-accent',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Theme</span>
              <div className="flex flex-wrap gap-2">
                {(['light', 'dark'] as const).map((option: Theme) => (
                  <button
                    key={option}
                    type="button"
                    data-testid={`admin-theme-${option}`}
                    aria-pressed={theme === option}
                    onClick={() => setTheme(option)}
                    className={cn(
                      'h-11 border px-4 text-[12px] font-bold uppercase tracking-[0.08em]',
                      theme === option
                        ? 'border-ink bg-ink text-ink-inverse'
                        : 'border-hairline bg-surface text-ink hover:border-accent',
                    )}
                  >
                    {option === 'light' ? 'Light' : 'Dark'}
                  </button>
                ))}
              </div>
            </div>

            <Field label="Timezone">
              <select
                data-testid="admin-timezone"
                value={timezone}
                onChange={(event) => {
                  setTimezone(event.target.value);
                  void save({ timezone: event.target.value });
                }}
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              >
                {[...new Set([timezone, ...TIMEZONES])].map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </select>
            </Field>
            <p className="text-[12px] text-ink-muted">
              Timestamps across the admin screens are read in this timezone.
            </p>
          </Panel>

          <Panel
            testId="admin-profile-session"
            title="Session"
            subtitle={`Signed in as ${user.email}`}
          >
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                data-testid="admin-profile-signout"
                onClick={() => {
                  session.logout();
                  navigate('/login', { replace: true });
                }}
              >
                Sign out
              </Button>
              <span className="text-[12px] text-ink-muted">
                {session.isDemo
                  ? 'Demo session — nothing is stored on a server.'
                  : 'Tokens are cleared from this device.'}
              </span>
            </div>
          </Panel>
        </div>
      </div>

      <Panel
        testId="admin-profile-policy"
        title="Policy acceptance"
        subtitle="The record created when this account was opened. The operator is held to the same documents as every customer."
      >
        {consents === null ? (
          <p className="text-[13px] text-ink-muted">Loading the acceptance record…</p>
        ) : consents.problem ? (
          <p data-testid="admin-profile-policy-error" className="text-[13px] text-danger">
            Could not load the acceptance record: {consents.problem.detail ?? consents.problem.title}.
          </p>
        ) : !consentList || consentList.data.length === 0 ? (
          <p data-testid="admin-profile-policy-empty" className="text-[13px] text-ink-muted">
            {session.isDemo
              ? 'Demo session — no server-side acceptance record exists.'
              : 'No acceptance is recorded on the server for this account.'}
          </p>
        ) : (
          <ul data-testid="admin-profile-policy-record" className="flex flex-col gap-3">
            {consentList.data.map((row) => (
              <li
                key={row.id}
                className="border-l-2 border-l-accent bg-page-subtle px-3 py-2 text-[13px] text-ink"
              >
                Accepted the Privacy Policy, Terms of Service and Health Disclaimer — policy version{' '}
                <span className="font-bold">{row.policyVersion}</span> — on{' '}
                <span className="font-bold">{acceptedDateLabel(row.acceptedAt)}</span>.
                {consentList.currentVersion && row.policyVersion !== consentList.currentVersion ? (
                  <span className="mt-1 block text-ink-muted">
                    The current version is {consentList.currentVersion}. This account has not accepted it.
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        <p className="text-[12px] text-ink-muted">
          Read the documents:{' '}
          <Link to={LEGAL_DOCS.health_disclaimer.path} className="font-bold text-accent hover:underline">
            Health Disclaimer
          </Link>
          {' · '}
          <Link to={LEGAL_DOCS.privacy_policy.path} className="font-bold text-accent hover:underline">
            Privacy Policy
          </Link>
          {' · '}
          <Link to={LEGAL_DOCS.terms_of_service.path} className="font-bold text-accent hover:underline">
            Terms
          </Link>
        </p>
      </Panel>

      <Panel
        testId="admin-profile-surfaces"
        title="What this account can reach"
        subtitle="The operator sees three products from one sign-in; each has its own entrance and its own rules."
      >
        <ul className="flex flex-col gap-2 text-[13px] text-ink-muted">
          <li className="flex items-start gap-2">
            <ShieldIcon className="mt-0.5 text-accent" />
            <span>
              This portal — accounts, businesses, money, plans, invitations, system health and the audit trail.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <ChatIcon className="h-4 w-4 text-accent" />
            <span>
              Help — every account’s support thread lands here. Answer it from the chat, in its own window.
            </span>
          </li>
        </ul>
        <div className="flex flex-wrap gap-2">
          <AppLinkButton to="/admin" variant="outline" size="sm">
            Platform overview
          </AppLinkButton>
          <AppLinkButton to="/admin/chat" variant="outline" size="sm">
            Open Help
          </AppLinkButton>
        </div>
        <p className="text-[12px] text-ink-muted">
          Plans, seats and access are set on the account and plan screens — never by this page.
        </p>
      </Panel>
    </div>
  );
}
